import i18next from "i18next";

import { ILanguageResponse } from "../@types/Translations";
import { translationService } from "../services/translationsService";

export const DEFAULT_LANGUAGE_CODE = "en-GB";

// A region subtag is either two letters (de-DE) or three digits (es-419).
const REGION_PATTERN = /^([a-z]{2}|\d{3})$/i;
const LANGUAGE_PATTERN = /^[a-z]{2,3}$/i;

/**
 * Normalises a language tag that reached us from outside the portal — the
 * `language` query parameter the mobile app puts on the webview URL, or a
 * stored user preference.
 *
 * The mobile app builds that parameter by interpolating the device locale
 * (`'${locale.languageCode}-${locale.countryCode}'`), so a device whose
 * language carries no region yields the literal string "de-null" (EN-5224).
 * i18next stores a bundle under the raw tag but resolves lookups against the
 * canonical one — "de-null" canonicalises to "de-Null" — so the bundle is never
 * found and every t() call renders its key on screen. Dropping the bogus region
 * and canonicalising here keeps the tag we register and the tag we look up
 * identical.
 *
 * Returns undefined when nothing usable can be salvaged, so the caller can fall
 * back to the default language.
 */
export const normaliseLanguageCode = (
  rawLanguageCode?: string | null
): string | undefined => {
  if (!rawLanguageCode) return undefined;

  // Tolerate an Accept-Language style list ("de-DE,de;q=0.9") and underscores.
  const [candidate] = rawLanguageCode.split(",");
  const [tag] = candidate.split(";");
  const parts = tag.trim().replace(/_/g, "-").split("-");

  const language = parts[0]?.toLowerCase();
  if (!language || !LANGUAGE_PATTERN.test(language)) return undefined;

  // "null" and "undefined" arrive as literal text; so does any other junk in
  // the region slot. Anything that is not a real region subtag is dropped
  // rather than carried into the tag we resolve against.
  const region =
    parts[1] && REGION_PATTERN.test(parts[1]) ? parts[1] : undefined;
  const code = region ? `${language}-${region.toUpperCase()}` : language;

  try {
    return Intl.getCanonicalLocales(code)[0] ?? code;
  } catch {
    return code;
  }
};

/**
 * Picks the closest language the platform actually has translations for.
 *
 * An exact match wins; otherwise a regionless tag is widened to the first
 * supported tag sharing its language, so a German device with no region still
 * gets de-DE rather than falling through to English.
 */
export const pickSupportedLanguageCode = (
  languageCode: string | undefined,
  supportedLanguages: ILanguageResponse[]
): string | undefined => {
  if (!languageCode) return undefined;

  const codes = supportedLanguages
    .map((language) => language.code)
    .filter((code): code is string => Boolean(code));

  const exactMatch = codes.find(
    (code) => code.toLowerCase() === languageCode.toLowerCase()
  );
  if (exactMatch) return exactMatch;

  const [language] = languageCode.split("-");
  return codes.find(
    (code) => code.toLowerCase().split("-")[0] === language.toLowerCase()
  );
};

let supportedLanguagesRequest: Promise<ILanguageResponse[]> | undefined;

/** The language list rarely changes and both webviews need it — fetch it once. */
const getSupportedLanguages = async (): Promise<ILanguageResponse[]> => {
  if (!supportedLanguagesRequest) {
    supportedLanguagesRequest = translationService
      .getLanguages()
      .then((response) => response.data ?? [])
      .catch(() => []);
  }

  return supportedLanguagesRequest;
};

/** Test seam — the cached language list outlives a single test otherwise. */
export const resetSupportedLanguagesCache = () => {
  supportedLanguagesRequest = undefined;
};

/**
 * Applies the language a webview was opened with and returns the tag to use for
 * both the fixed-translations request and `i18n.addResourceBundle`.
 *
 * Callers must use the returned value for both: reading `i18n.language` back
 * out is what let the registered bundle and the resolved bundle drift apart.
 */
export const applyWebViewLanguage = async (
  i18n: typeof i18next,
  rawLanguageCode?: string | null
): Promise<string> => {
  const normalisedLanguageCode = normaliseLanguageCode(rawLanguageCode);

  const supportedLanguageCode = pickSupportedLanguageCode(
    normalisedLanguageCode,
    await getSupportedLanguages()
  );

  const languageCode =
    supportedLanguageCode ?? normalisedLanguageCode ?? DEFAULT_LANGUAGE_CODE;

  await i18n.changeLanguage(languageCode);

  return i18n.resolvedLanguage ?? i18n.language ?? languageCode;
};
